import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "../../annotations";
import { CancelRequestOptions } from "./CancelRequestOptions";
import {
  VideoProjectQueueAssignmentsPayload,
  VideoProjectQueueAssignmentsUpdateRequest,
  Media,
  Page,
  VideoCatalystUsageReport,
  VideoProject,
  VideoProjectQueueSearchParams,
} from "./dtos";
import { SocialNetworksId } from "@/interfaces";

const VIDEO_PROJECTS_SERVICE = `${Constants.API_URL}/bedrock/video_projects`;

type ExtraField = keyof VideoProject.ExtraFields;
type ExtraFields = ExtraField | ExtraField[] | { readonly [key: string]: unknown };

interface VideoProjectRequest {
  playerId?: number;
  preferredPostDate?: string;
  socialNetworkIds?: SocialNetworksId[];
  messageToPost?: string;
  originalMedia: Media[];
  isAutoPost?: boolean;
  autoCaption?: boolean;
  allowPlayerHashtags?: boolean;
  allowShortLink?: boolean;
  allowCompliance?: boolean;
  hashtags?: string;
  initialMessages?: string[];
  videoScriptId?: number | null;
  contentPageId?: number | null;
  preferences?: Omit<VideoProject.Preferences, "id" | "videoProjectId">;
  source?: VideoProject.Source;
}

export interface VideoCatalystUsageReportParams {
  startDate?: string;
  endDate?: string;
}

interface VideoProjectsQuery extends VideoProjectQueueSearchParams {
  extraFields?: ExtraFields;
}

export class VideoProjectServices {
  @scCatchServiceException()
  static async create(request: VideoProjectRequest, extraFields?: ExtraFields): Promise<VideoProject> {
    const { data } = await axios.post(`${VIDEO_PROJECTS_SERVICE}`, request, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async index(
    params: VideoProjectsQuery = {},
    options?: CancelRequestOptions
  ): Promise<Page<VideoProject, VideoProject.Pagination>> {
    const { data } = await axios.get(VIDEO_PROJECTS_SERVICE, { params, ...options });
    return data;
  }

  @scCatchServiceException()
  static async show(
    id: string | number,
    extraFields?: ExtraFields,
    options?: CancelRequestOptions
  ): Promise<VideoProject> {
    const { data } = await axios.get(`${VIDEO_PROJECTS_SERVICE}/${id}`, { params: { extraFields }, ...options });
    return data;
  }

  @scCatchServiceException()
  static async update(
    id: string | number,
    request: VideoProject.EventRequest,
    extraFields?: ExtraFields
  ): Promise<VideoProject> {
    const { data } = await axios.patch(`${VIDEO_PROJECTS_SERVICE}/${id}`, request, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async getCreditUsageReport(params?: VideoCatalystUsageReportParams): Promise<VideoCatalystUsageReport> {
    const { data } = await axios.get<VideoCatalystUsageReport>(`${VIDEO_PROJECTS_SERVICE}/credit_usage_report`, {
      params,
    });
    return data;
  }

  @scCatchServiceException()
  static async acknowledge(
    id: string | number,
    ackType: VideoProject.AckType = VideoProject.AckType.MESSAGES
  ): Promise<void> {
    await axios.put(`${VIDEO_PROJECTS_SERVICE}/${id}/acknowledge/${ackType}`);
  }

  @scCatchServiceException()
  static async sendMessage(id: string | number, content: string): Promise<VideoProject.Message> {
    const { data } = await axios.post(`${VIDEO_PROJECTS_SERVICE}/${id}/messages`, { content });
    return data;
  }

  @scCatchServiceException()
  static async upsertClientNotes(request: {
    notes: string;
    playerId?: number;
    coachId?: number | string;
  }): Promise<VideoProject.ClientNote> {
    const { data } = await axios.post(`${Constants.API_URL}/bedrock/video_project_client_notes`, {
      notes: request.notes,
      playerId: request.playerId,
      coachId: request.coachId != null ? Number(request.coachId) : undefined,
    });
    return data;
  }

  @scCatchServiceException()
  static async getCoachQueueAssignments(coachId: number): Promise<VideoProjectQueueAssignmentsPayload> {
    const { data } = await axios.get<VideoProjectQueueAssignmentsPayload>(
      `${Constants.API_URL}/bedrock/coaches/${coachId}/video_queue_assignments`
    );
    return data;
  }

  @scCatchServiceException()
  static async updateCoachQueueAssignments(
    coachId: number,
    request: VideoProjectQueueAssignmentsUpdateRequest
  ): Promise<VideoProjectQueueAssignmentsPayload> {
    const { data } = await axios.put<VideoProjectQueueAssignmentsPayload>(
      `${Constants.API_URL}/bedrock/coaches/${coachId}/video_queue_assignments`,
      request
    );
    return data;
  }
}
